import React, {useCallback, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import type {PreviewStreamController} from '@pexip/media';
import {DeniedDevices} from '@pexip/media';
import {reverseDeviceId} from '@pexip/media-control';
import {
    AudioMeter,
    DevicesList,
    PanelHeader,
    SelfViewSettings,
    SettingsPanel,
    TestId as MediaTestId,
    type DeviceStatusInfo,
    useDeviceStatusInfo,
    usePreviewAnalyzer,
    usePreviewControllerHandler,
    usePreviewErrorHandling,
} from '@pexip/media-components';
import {
    Bar,
    Button,
    List,
    ThemeProvider,
    notificationToastSignal,
    withColorScheme,
} from '@pexip/components';
import {noop} from '@pexip/utils';

import {useConfig} from '../config';
import {
    usePreviewAudioInput,
    usePreviewAudioOutput,
    usePreviewStreamQuality,
    usePreviewVideoInput,
} from '../services/Media.service';
import {TestId} from '../../test/testIds';
import {useSelfviewMirrorMode} from '../hooks/useSelfviewMirrorMode';
import {
    useAudioOutput,
    useDevices,
    useHaveRequestedMedia,
    useMediaRequest,
} from '../hooks/userMedia';

import {OutputAudioTester} from './OutputAudioTester.viewModel';

const SecondaryAudioMeter = withColorScheme(AudioMeter, 'light');

export const MediaControl: React.FC<{
    onBackClick: () => void;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    controller: PreviewStreamController;
}> = ({close, onBackClick, controller}) => {
    const {t} = useTranslation();
    const devices = useDevices();
    const [settingsChanged, setSettingsChanged] = useState(false);
    const [isMirrored] = useSelfviewMirrorMode();
    const requested = useHaveRequestedMedia();
    const {
        audioMuted,
        audioSuspended,
        handleSave,
        isSaving,
        previewMedia,
        status,
        updatingPreviewAudio,
        updatingPreviewVideo,
        videoInputChanged,
        videoMuted,
        videoSuspended,
    } = usePreviewControllerHandler({
        controller,
        applyChanges: () => {
            videoInput.applyChanges();
            audioInput.applyChanges();
            audioOutput.applyChanges();
            return streamQuality.applyChanges();
        },
    });
    const deviceStatusInfo = useDeviceStatusInfo(status, requested);

    const {
        videoInputError,
        setVideoInputError,
        audioInputError,
        setAudioInputError,
    } = usePreviewErrorHandling(controller, status, requested);

    const audioInput = usePreviewAudioInput({
        error: audioInputError,
        controller,
        setError: setAudioInputError,
    });

    const videoInput = usePreviewVideoInput({
        error: videoInputError,
        controller,
        setError: setVideoInputError,
    });
    const mainAudioOutput = useAudioOutput();
    const audioOutput = usePreviewAudioOutput(devices, mainAudioOutput);

    const streamQuality = usePreviewStreamQuality();
    const request = useMediaRequest();
    const [allowChangeOfMediaDirection] = useConfig(
        'allowChangeOfMediaDirection',
    );

    useEffect(
        () =>
            controller.onApplyChangesError(() =>
                notificationToastSignal.emit([
                    {
                        message: t(
                            'settings.video-and-sound-section.apply-changes-error',
                            'There was a problem applying the Video and sound changes',
                        ),
                        timeout: 0,
                        isClickable: true,
                        isDanger: true,
                        isInterrupt: true,
                        onDismiss: noop,
                    },
                ]),
            ),
        [controller, t],
    );

    useEffect(() => {
        setSettingsChanged(
            controller.audioInputChanged ||
                controller.videoInputChanged ||
                audioOutput.hasChanges() ||
                streamQuality.hasChanges(),
        );
    }, [
        audioOutput,
        controller.audioInputChanged,
        controller.videoInputChanged,
        streamQuality,
    ]);

    const analyzer = usePreviewAnalyzer(controller);

    const requestedAudio = Boolean(previewMedia?.getConstraints()?.audio);

    const requestedVideo = Boolean(previewMedia?.getConstraints()?.video);

    const handleAudioOutputChange = (deviceId: string) => {
        const device = reverseDeviceId(deviceId);
        const [selectedDevice] = devices.get(device);
        if (selectedDevice) {
            audioOutput.setPreview(selectedDevice);
        }
    };

    const deviceList = (
        <DevicesList
            devices={devices}
            disableVideoInputSelector={
                (!allowChangeOfMediaDirection.video && !request.video) ||
                updatingPreviewVideo
            }
            disableAudioInputSelector={
                (!allowChangeOfMediaDirection.audio && !request.audio) ||
                updatingPreviewAudio
            }
            disableAudioOutputSelector={false}
            requestedAudio={requestedAudio}
            requestedVideo={requestedVideo}
            videoInputError={(request.video && videoInputError) || undefined}
            audioInputError={(request.audio && audioInputError) || undefined}
            videoInput={videoInput.preview}
            audioInput={audioInput.preview}
            audioOutput={audioOutput.preview}
            onAudioInputChange={audioInput.setPreview}
            onAudioOutputChange={handleAudioOutputChange}
            onVideoInputChange={videoInput.setPreview}
            isLoading={isSaving}
        />
    );

    const audioMeter = !audioSuspended &&
        !audioMuted &&
        !!previewMedia?.audioInput &&
        !!analyzer && (
            <SecondaryAudioMeter
                analyzer={analyzer}
                data-testid={TestId.AudioMeterSettings}
            />
        );

    const allowToSave =
        settingsChanged &&
        !isSaving &&
        /*
         * Respective device selection is disabled when requestedVideo or requestedAudio
         * is false, this should not hinder the user from saving other changes.
         */
        (!videoInput.error.title || !requestedVideo) &&
        (!audioInput.error.title || !requestedAudio);

    const onSave = useCallback(
        (e: React.SyntheticEvent<HTMLElement, Event>) => {
            void handleSave(e);
            setSettingsChanged(false);
        },
        [handleSave],
    );

    const previewStream = previewMedia?.stream;
    const requestCameraPreview = useCallback(() => {
        if (controller.media?.videoInput) {
            if (
                controller.media.getVideoTracks().some(track => track.stopped)
            ) {
                controller.updatePreviewInput(controller.media.videoInput);
            } else {
                controller.media.muteVideo(false);
            }
        } else if (controller.media?.expectedVideoInput) {
            controller.updatePreviewInput(controller.media.expectedVideoInput);
        }
    }, [controller]);
    const stopCameraPreview = useCallback(() => {
        controller.media?.muteVideo(true);
    }, [controller]);

    let mergedDeviceStatusInfo: DeviceStatusInfo = {
        title: t(
            'settings.turn-on-preview-titile',
            'Want to see your camera preview?',
        ),
        message: t(
            'settings.turn-on-preview-message',
            'To show you a preview the App needs to request access to your camera.',
        ),
    };
    if (deviceStatusInfo.error) {
        mergedDeviceStatusInfo = deviceStatusInfo;
    } else if (videoInputError.title) {
        mergedDeviceStatusInfo = {
            title: videoInputError.title,
            message: videoInputError.description ?? '',
            error: DeniedDevices.Camera,
        };
    }

    return (
        <SettingsPanel
            scrollable
            data-testid={TestId.VideoAndSoundPanel}
            headerContent={
                <ThemeProvider colorScheme="light">
                    <PanelHeader
                        title={`${t(
                            'settings.video-and-sound',
                            'Video and sound',
                        )}`}
                        onBackClick={onBackClick}
                        backButtonTestId={MediaTestId.ButtonSettingsBack}
                    />
                </ThemeProvider>
            }
            footerContent={
                <ThemeProvider colorScheme="light">
                    <Bar>
                        <Button
                            onClick={close}
                            variant="secondary"
                            size="medium"
                            modifier="fullWidth"
                            isDisabled={isSaving}
                            data-testid={TestId.ButtonSettingsCancel}
                        >
                            <Trans t={t} i18nKey="settings.cancel">
                                Cancel
                            </Trans>
                        </Button>
                        <Button
                            onClick={onSave}
                            type="submit"
                            modifier="fullWidth"
                            className="ms-2"
                            isLoading={isSaving}
                            isDisabled={!allowToSave}
                            data-testid={TestId.ButtonSettingsSave}
                        >
                            <Trans t={t} i18nKey="settings.save">
                                Save
                            </Trans>
                        </Button>
                    </Bar>
                </ThemeProvider>
            }
        >
            <ThemeProvider colorScheme="light">
                <SelfViewSettings
                    deviceStatusInfo={mergedDeviceStatusInfo}
                    isDisabled={
                        (!allowChangeOfMediaDirection.video &&
                            !request.video) ||
                        videoSuspended
                    }
                    isMirrored={isMirrored}
                    isVideoInputMuted={videoMuted || !!videoInputError.title}
                    localMediaStream={previewStream}
                    requestCameraPreview={requestCameraPreview}
                    showPreviewLabel={
                        !(videoMuted || !!videoInputError.title) &&
                        videoInputChanged
                    }
                    stopCameraPreview={stopCameraPreview}
                    updatingPreviewAudio={updatingPreviewAudio}
                    updatingPreviewVideo={updatingPreviewVideo}
                >
                    {audioMeter}
                </SelfViewSettings>
                {deviceList}
                <List spacing="none" className="mt-1">
                    <OutputAudioTester
                        sinkId={audioOutput.preview?.deviceId ?? ''}
                    />
                </List>
            </ThemeProvider>
        </SettingsPanel>
    );
};
